/**
 * DetailDrawerContext - Coordinates a page-level slide-over drawer for showing
 * entity detail views (e.g. TaskDetail). When the drawer is open, floating
 * side panels should hide to avoid visual conflict.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

interface DetailDrawerContextValue {
  /** Whether the drawer is currently open. */
  isOpen: boolean;
  /** The content currently displayed in the drawer. */
  content: React.ReactNode;
  /** Increments each time openDrawer is called (used to reset scroll). */
  contentKey: number;
  /** Open the drawer with the given content. */
  openDrawer: (content: React.ReactNode) => void;
  /** Close the drawer. */
  closeDrawer: () => void;
}

const DetailDrawerContext = createContext<DetailDrawerContextValue | null>(null);

export function useDetailDrawer(): DetailDrawerContextValue {
  const ctx = useContext(DetailDrawerContext);
  if (!ctx) {
    throw new Error('useDetailDrawer must be used within a <DetailDrawerProvider>');
  }
  return ctx;
}

/**
 * Optional hook that returns whether a drawer is open. Returns false if no
 * provider is present (safe to use in components that may or may not be
 * inside a DetailDrawerProvider).
 */
export function useIsDrawerOpen(): boolean {
  const ctx = useContext(DetailDrawerContext);
  return ctx?.isOpen ?? false;
}

export const DetailDrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [content, setContent] = useState<React.ReactNode>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [contentKey, setContentKey] = useState(0);
  const contentKeyRef = useRef(0);

  const openDrawer = useCallback((node: React.ReactNode) => {
    contentKeyRef.current += 1;
    setContentKey(contentKeyRef.current);
    setContent(node);
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    // Clear content after animation completes
    setTimeout(() => setContent(null), 300);
  }, []);

  const value = useMemo(
    () => ({ isOpen, content, contentKey, openDrawer, closeDrawer }),
    [isOpen, content, contentKey, openDrawer, closeDrawer],
  );

  return (
    <DetailDrawerContext.Provider value={value}>
      {children}
    </DetailDrawerContext.Provider>
  );
};
